import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle,
  Download,
  FileSpreadsheet,
  FileText,
  Loader,
  Search,
  Waves,
} from "lucide-react";

const MAX_RANGE_DAYS = 60;
const IST_TIME_ZONE = "Asia/Kolkata";
const PDF_PAGE = { width: 841.89, height: 595.28, margin: 30 };

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 6);
  return toDateInputValue(date);
};

const getRangeDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  return Math.floor((end - start) / 86400000) + 1;
};

const formatHumanDate = (value) => {
  if (!value) return "-";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value);
  const [, year, month, day] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
  ).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatIstDownloadDateTime = () =>
  new Date().toLocaleString("en-IN", {
    timeZone: IST_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

const formatNumber = (value, digits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return numericValue.toFixed(digits);
};

const formatPercent = (value, digits = 2) => {
  const formatted = formatNumber(value, digits);
  return formatted === "-" ? "-" : `${formatted}%`;
};

const sanitizeFileName = (value) =>
  String(value || "alert-events")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatExportValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const hasConfiguredThresholds = (asset) =>
  ["low", "med", "high"].every((key) => {
    const value = asset?.[key];
    return value !== null && value !== undefined && value !== "";
  });

const getAssetDisplayName = (asset, fallback = "-") => {
  if (!asset) return fallback;
  const name =
    asset.name || asset.asset_name || asset.tank_name || asset.asset_id;
  const id = asset.asset_id || asset.tank_id || asset.id;
  return name && id && name !== id ? `${name} (${id})` : name || id || fallback;
};

const csvEscape = (value) => {
  const text = formatExportValue(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const escapeXml = (value) =>
  formatExportValue(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const downloadBlob = (content, fileName, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const XLSX_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
};

const concatBytes = (parts) => {
  const length = parts.reduce((total, part) => total + part.length, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  parts.forEach((part) => {
    output.set(part, offset);
    offset += part.length;
  });
  return output;
};

const createStoredZip = (files) => {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach(({ path, content }) => {
    const nameBytes = encoder.encode(path);
    const contentBytes =
      content instanceof Uint8Array ? content : encoder.encode(content);
    const checksum = crc32(contentBytes);

    const localHeader = new Uint8Array(30);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0, true);
    localView.setUint32(14, checksum, true);
    localView.setUint32(18, contentBytes.length, true);
    localView.setUint32(22, contentBytes.length, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);

    const centralHeader = new Uint8Array(46);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0, true);
    centralView.setUint32(16, checksum, true);
    centralView.setUint32(20, contentBytes.length, true);
    centralView.setUint32(24, contentBytes.length, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, offset, true);

    localParts.push(localHeader, nameBytes, contentBytes);
    centralParts.push(centralHeader, nameBytes);
    offset += localHeader.length + nameBytes.length + contentBytes.length;
  });

  const centralOffset = offset;
  const centralDirectory = concatBytes(centralParts);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralDirectory.length, true);
  endView.setUint32(16, centralOffset, true);
  endView.setUint16(20, 0, true);

  return new Blob([...localParts, centralDirectory, endRecord], {
    type: XLSX_TYPE,
  });
};

const getColumnName = (index) => {
  let name = "";
  let value = index + 1;
  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }
  return name;
};

const createXlsxCell = (cell, rowNumber, columnIndex) => {
  const value = cell?.value ?? cell;
  const style = Number.isFinite(cell?.style) ? ` s="${cell.style}"` : "";
  const reference = `${getColumnName(columnIndex)}${rowNumber}`;
  return `<c r="${reference}" t="inlineStr"${style}><is><t>${escapeXml(value).replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, " ")}</t></is></c>`;
};

const createWorksheetXml = (sheetRows) => {
  const maxColumns = Math.max(...sheetRows.map((row) => row.length), 1);
  const dimension = `A1:${getColumnName(maxColumns - 1)}${sheetRows.length}`;
  const widths = [28, 34, 18, 16, 24, 28];
  const columnXml = Array.from({ length: maxColumns }, (_, index) => {
    const width = widths[index] || 20;
    return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
  }).join("");
  const rowsXml = sheetRows
    .map((row, index) => {
      const rowNumber = index + 1;
      const cells = row
        .map((cell, columnIndex) =>
          createXlsxCell(cell, rowNumber, columnIndex),
        )
        .join("");
      return `<row r="${rowNumber}">${cells}</row>`;
    })
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="${dimension}"/>
  <sheetViews><sheetView workbookViewId="0"/></sheetViews>
  <cols>${columnXml}</cols>
  <sheetData>${rowsXml}</sheetData>
</worksheet>`;
};

const XLSX_STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="4">
    <font><sz val="11"/><color rgb="FF111827"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><color rgb="FF0F4C75"/><name val="Calibri"/></font>
    <font><b/><sz val="14"/><color rgb="FF0F4C75"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
  </fonts>
  <fills count="5">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFEAF6FF"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF0F4C75"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF8FBFF"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFD9E2EC"/></left>
      <right style="thin"><color rgb="FFD9E2EC"/></right>
      <top style="thin"><color rgb="FFD9E2EC"/></top>
      <bottom style="thin"><color rgb="FFD9E2EC"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="5">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
    <xf numFmtId="0" fontId="3" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
    <xf numFmtId="0" fontId="0" fillId="4" borderId="1" xfId="0" applyFill="1" applyBorder="1"/>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const createXlsxReport = ({ title, metadata, columns, rows }) => {
  const sheetRows = [
    [{ value: title, style: 2 }],
    [],
    ...metadata.map((item) => [
      { value: item.label, style: 1 },
      { value: item.value, style: 0 },
    ]),
    [],
    columns.map((column) => ({ value: column.label, style: 3 })),
    ...rows.map((row, index) =>
      columns.map((column) => ({
        value: column.getValue(row),
        style: index % 2 ? 4 : 0,
      })),
    ),
  ];

  return createStoredZip([
    {
      path: "[Content_Types].xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`,
    },
    {
      path: "_rels/.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      path: "xl/workbook.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="Alert Events" sheetId="1" r:id="rId1"/></sheets>
</workbook>`,
    },
    {
      path: "xl/_rels/workbook.xml.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    {
      path: "xl/styles.xml",
      content: XLSX_STYLES,
    },
    {
      path: "xl/worksheets/sheet1.xml",
      content: createWorksheetXml(sheetRows),
    },
  ]);
};

const pdfText = (value) =>
  formatExportValue(value)
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapePdfText = (value) =>
  pdfText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const estimateTextWidth = (text, fontSize) =>
  pdfText(text).length * fontSize * 0.5;

const wrapPdfText = (value, maxWidth, fontSize) => {
  const text = pdfText(value);
  if (!text) return ["-"];

  const words = text.split(" ");
  const lines = [];
  let line = "";

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (estimateTextWidth(candidate, fontSize) <= maxWidth) {
      line = candidate;
      return;
    }

    if (line) {
      lines.push(line);
      line = "";
    }

    if (estimateTextWidth(word, fontSize) <= maxWidth) {
      line = word;
      return;
    }

    let chunk = "";
    word.split("").forEach((char) => {
      const nextChunk = `${chunk}${char}`;
      if (estimateTextWidth(nextChunk, fontSize) <= maxWidth) {
        chunk = nextChunk;
      } else {
        if (chunk) lines.push(chunk);
        chunk = char;
      }
    });
    line = chunk;
  });

  if (line) lines.push(line);
  return lines.length ? lines : ["-"];
};

const createPdfReport = ({ title, metadata, columns, rows }) => {
  const { width, height, margin } = PDF_PAGE;
  const tableWidth = width - margin * 2;
  const tableSize = 7;
  const lineHeight = tableSize + 2.4;
  const columnWidths = [120, 150, 84, 88, 150, tableWidth - 592];
  const pages = [[]];
  let pageIndex = 0;
  let y = margin;

  const currentPage = () => pages[pageIndex];
  const push = (command) => currentPage().push(command);
  const addPage = () => {
    pages.push([]);
    pageIndex += 1;
    y = margin;
  };
  const ensureSpace = (space) => {
    if (y + space > height - margin) addPage();
  };
  const setFillColor = (color) =>
    push(`${color.map((v) => v.toFixed(3)).join(" ")} rg`);
  const drawRect = (x, topY, rectWidth, rectHeight, color) => {
    setFillColor(color);
    push(
      `${x.toFixed(2)} ${(height - topY - rectHeight).toFixed(2)} ${rectWidth.toFixed(2)} ${rectHeight.toFixed(2)} re f`,
    );
  };
  const drawText = (
    text,
    x,
    topY,
    size = tableSize,
    font = "F1",
    color = [0.08, 0.11, 0.18],
  ) => {
    setFillColor(color);
    push(
      `BT /${font} ${size} Tf ${x.toFixed(2)} ${(height - topY - size).toFixed(2)} Td (${escapePdfText(text)}) Tj ET`,
    );
  };
  const drawTableHeader = () => {
    const headerLines = columns.map((column, index) =>
      wrapPdfText(column.label, columnWidths[index] - 6, tableSize),
    );
    const headerHeight =
      Math.max(...headerLines.map((lines) => lines.length)) * lineHeight + 9;
    ensureSpace(headerHeight + 18);
    drawRect(margin, y, tableWidth, headerHeight, [0.04, 0.36, 0.56]);
    let x = margin;
    headerLines.forEach((lines, index) => {
      lines.forEach((line, lineIndex) => {
        drawText(
          line,
          x + 4,
          y + 5 + lineIndex * lineHeight,
          tableSize,
          "F2",
          [1, 1, 1],
        );
      });
      x += columnWidths[index];
    });
    y += headerHeight;
  };

  drawRect(margin, y, tableWidth, 28, [0.9, 0.97, 1]);
  drawText(title, margin + 10, y + 8, 14, "F2", [0.03, 0.27, 0.43]);
  y += 40;

  const metaColumns = 3;
  const metaGap = 8;
  const metaWidth = (tableWidth - metaGap * (metaColumns - 1)) / metaColumns;
  metadata.forEach((item, index) => {
    const col = index % metaColumns;
    if (col === 0 && index > 0) y += 38;
    ensureSpace(44);
    const x = margin + col * (metaWidth + metaGap);
    drawRect(x, y, metaWidth, 30, [0.95, 0.98, 1]);
    drawRect(x, y, 3, 30, [0.05, 0.57, 0.82]);
    drawText(item.label, x + 8, y + 6, 6.8, "F2", [0.26, 0.36, 0.48]);
    wrapPdfText(item.value, metaWidth - 14, 7.2)
      .slice(0, 2)
      .forEach((line, lineIndex) => {
        drawText(line, x + 8, y + 17 + lineIndex * 8, 7.2);
      });
  });
  y += 46;
  drawTableHeader();

  rows.forEach((row, rowIndex) => {
    const cellLines = columns.map((column, index) =>
      wrapPdfText(column.getValue(row), columnWidths[index] - 6, tableSize),
    );
    const rowHeight =
      Math.max(...cellLines.map((lines) => lines.length)) * lineHeight + 9;

    if (y + rowHeight > height - margin) {
      addPage();
      drawTableHeader();
    }

    drawRect(
      margin,
      y,
      tableWidth,
      rowHeight,
      rowIndex % 2 ? [0.96, 0.98, 1] : [1, 1, 1],
    );
    let x = margin;
    cellLines.forEach((lines, index) => {
      lines.forEach((line, lineIndex) => {
        drawText(line, x + 4, y + 5 + lineIndex * lineHeight, tableSize);
      });
      x += columnWidths[index];
    });
    y += rowHeight;
  });

  const objects = [];
  const addObject = (content) => {
    objects.push(content);
    return objects.length;
  };
  const catalogId = addObject("<< /Type /Catalog /Pages 2 0 R >>");
  const pagesId = addObject("");
  const fontId = addObject(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
  );
  const boldFontId = addObject(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>",
  );
  const pageIds = [];

  pages.forEach((commands) => {
    const stream = commands.join("\n");
    const contentId = addObject(
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    );
    const pageId = addObject(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${width} ${height}] /Resources << /Font << /F1 ${fontId} 0 R /F2 ${boldFontId} 0 R >> >> /Contents ${contentId} 0 R >>`,
    );
    pageIds.push(pageId);
  });

  objects[pagesId - 1] =
    `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
};

const getStageStyles = (type) => {
  switch (type) {
    case "critical":
      return {
        badge: "bg-red-100 text-red-700 border-red-200",
        icon: "text-red-500",
        card: "border-red-200 bg-red-50 text-red-700",
      };
    case "low":
      return {
        badge: "bg-amber-100 text-amber-700 border-amber-200",
        icon: "text-amber-500",
        card: "border-amber-200 bg-amber-50 text-amber-700",
      };
    case "full":
      return {
        badge: "bg-blue-100 text-blue-700 border-blue-200",
        icon: "text-blue-500",
        card: "border-blue-200 bg-blue-50 text-blue-700",
      };
    default:
      return {
        badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
        icon: "text-emerald-500",
        card: "border-emerald-200 bg-emerald-50 text-emerald-700",
      };
  }
};

const Alerts = () => {
  const [societyInfo, setSocietyInfo] = useState(null);
  const [storageAssets, setStorageAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState("");
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));
  const [report, setReport] = useState(null);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  const events = report?.events || [];
  const selectedAssetInfo = storageAssets.find(
    (asset) => asset.asset_key === selectedAsset,
  );
  const selectedTowerName =
    selectedAssetInfo?.tower_name || report?.tower_name || "-";
  const selectedTowerId =
    selectedAssetInfo?.tower_id || report?.tower_id || "-";
  const selectedAssetName =
    selectedAssetInfo?.name || report?.asset_name || report?.tank_name || "-";
  const selectedAssetId =
    selectedAssetInfo?.asset_id || report?.asset_id || report?.tank_id || "-";
  const selectedAssetType =
    selectedAssetInfo?.asset_type_label || report?.asset_type_label || "-";
  const selectedThresholds = {
    critical: report?.thresholds?.critical ?? 0,
    low: report?.thresholds?.low ?? selectedAssetInfo?.low,
    normal: report?.thresholds?.normal ?? selectedAssetInfo?.med,
    full: report?.thresholds?.full ?? selectedAssetInfo?.high,
  };
  const thresholdItems = [
    { type: "critical", label: "Critical", value: selectedThresholds.critical },
    { type: "low", label: "Low", value: selectedThresholds.low },
    { type: "normal", label: "Mid", value: selectedThresholds.normal },
    { type: "full", label: "High", value: selectedThresholds.full },
  ];

  const columns = useMemo(
    () => [
      { label: "Stage", getValue: (row) => row.stage_label },
      { label: "Timestamp (IST)", getValue: (row) => row.datetime },
      { label: "Level", getValue: (row) => formatPercent(row.percentage) },
      { label: "Threshold", getValue: (row) => formatPercent(row.threshold) },
      { label: "Asset Type", getValue: () => selectedAssetType },
      {
        label: "Storage Asset",
        getValue: () => `${selectedAssetName} (${selectedAssetId})`,
      },
    ],
    [selectedAssetId, selectedAssetName, selectedAssetType],
  );

  const getExportMetadata = () => [
    {
      label: "Society",
      value: report?.society_name || societyInfo?.name || "-",
    },
    {
      label: "Tower",
      value:
        selectedTowerId === "-"
          ? "-"
          : `${selectedTowerName} (${selectedTowerId})`,
    },
    {
      label: "Storage Asset",
      value: `${selectedAssetName} (${selectedAssetId})`,
    },
    { label: "Asset Type", value: selectedAssetType },
    {
      label: "Date Range",
      value: `${formatHumanDate(startDate)} to ${formatHumanDate(endDate)}`,
    },
    { label: "Downloaded At (IST)", value: formatIstDownloadDateTime() },
    {
      label: "Capacity",
      value: report?.capacity ? `${formatNumber(report.capacity, 0)} L` : "-",
    },
    {
      label: "Critical Threshold",
      value: formatPercent(selectedThresholds.critical),
    },
    { label: "Low Threshold", value: formatPercent(selectedThresholds.low) },
    { label: "Mid Threshold", value: formatPercent(selectedThresholds.normal) },
    { label: "High Threshold", value: formatPercent(selectedThresholds.full) },
    { label: "Event Count", value: events.length },
  ];

  const fileName = sanitizeFileName(
    `storage-asset-threshold-events-${selectedAssetId}-${startDate}-to-${endDate}`,
  );

  useEffect(() => {
    const loadStorageAssets = async () => {
      setLoadingFilters(true);
      try {
        const response = await api.getStageStorageAssets();
        const nextAssets = Array.isArray(response?.storage_assets)
          ? response.storage_assets
          : Array.isArray(response?.tanks)
            ? response.tanks
            : [];
        setSocietyInfo({
          id: response?.society_id,
          name: response?.society_name,
        });
        setStorageAssets(nextAssets);
        setSelectedAsset(
          nextAssets.length === 1 ? nextAssets[0].asset_key : "",
        );
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Failed to load storage assets"));
        setStorageAssets([]);
        setSelectedAsset("");
      } finally {
        setLoadingFilters(false);
      }
    };

    loadStorageAssets();
  }, []);

  const validateFilters = () => {
    if (!selectedAsset) {
      toast.error("Please choose a storage asset");
      return false;
    }
    if (selectedAssetInfo && !hasConfiguredThresholds(selectedAssetInfo)) {
      toast.error("No threshold set for this storage asset");
      return false;
    }
    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return false;
    }

    const days = getRangeDays(startDate, endDate);
    if (days <= 0) {
      toast.error("End date must be greater than or equal to start date");
      return false;
    }
    if (days > MAX_RANGE_DAYS) {
      toast.error("Date range cannot exceed 60 days");
      return false;
    }

    return true;
  };

  const loadStageEvents = async () => {
    if (!validateFilters()) return;

    setLoadingReport(true);
    try {
      const response = await api.getStageEvents(
        selectedAsset,
        startDate,
        endDate,
      );
      setReport(response);
      if ((response.events || []).length === 0) {
        toast.info("No daily threshold hits found for this date range");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to load alert events"));
      setReport(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const ensureExportData = () => {
    if (!events.length) {
      toast.error("No alert event data is available to export");
      return false;
    }
    return true;
  };

  const downloadCsv = () => {
    if (!ensureExportData()) return;
    const metadata = getExportMetadata();
    const rows = [
      ["Report", "Storage Asset Daily Threshold Events"],
      ...metadata.map((item) => [item.label, item.value]),
      [],
      columns.map((column) => column.label),
      ...events.map((event) => columns.map((column) => column.getValue(event))),
    ];
    const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\r\n");
    downloadBlob(`\uFEFF${csv}`, `${fileName}.csv`, "text/csv;charset=utf-8;");
  };

  const downloadExcel = () => {
    if (!ensureExportData()) return;
    const metadata = getExportMetadata();
    const workbook = createXlsxReport({
      title: "Storage Asset Daily Threshold Events",
      metadata,
      columns,
      rows: events,
    });
    downloadBlob(workbook, `${fileName}.xlsx`, XLSX_TYPE);
  };

  const downloadPdf = () => {
    if (!ensureExportData()) return;
    const metadata = getExportMetadata();
    const pdf = createPdfReport({
      title: "Storage Asset Daily Threshold Events",
      metadata,
      columns,
      rows: events,
    });
    downloadBlob(pdf, `${fileName}.pdf`, "application/pdf");
  };

  const summaryItems = [
    {
      type: "critical",
      label: "Critical",
      count: report?.summary?.critical || 0,
    },
    { type: "low", label: "Low", count: report?.summary?.low || 0 },
    { type: "normal", label: "Mid", count: report?.summary?.normal || 0 },
    { type: "full", label: "High", count: report?.summary?.full || 0 },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="alerts-page">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold text-slate-900">
              <Bell className="h-8 w-8 text-sky-600" />
              Alerts
            </h1>
            <p className="mt-1 text-slate-600">
              View the first daily storage asset threshold hits without normal
              sensor noise.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={downloadCsv}
              disabled={!events.length}
            >
              <Download className="mr-2 h-4 w-4" />
              CSV
            </Button>
            <Button
              variant="outline"
              onClick={downloadExcel}
              disabled={!events.length}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Excel
            </Button>
            <Button
              variant="outline"
              onClick={downloadPdf}
              disabled={!events.length}
            >
              <FileText className="mr-2 h-4 w-4" />
              PDF
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
            <CardDescription>
              Choose a storage asset and date range up to 60 days.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[1.5fr_1fr_1fr_auto]">
              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Waves className="h-4 w-4 text-slate-500" />
                  Storage Asset
                </label>
                <Select
                  value={selectedAsset}
                  onValueChange={(value) => {
                    setSelectedAsset(value);
                    setReport(null);
                  }}
                  disabled={loadingFilters}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        loadingFilters
                          ? "Loading storage assets..."
                          : "Choose storage asset"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {storageAssets.map((asset) => (
                      <SelectItem key={asset.asset_key} value={asset.asset_key}>
                        {[
                          getAssetDisplayName(asset),
                          asset.asset_type_label,
                          asset.tower_name,
                        ]
                          .filter(Boolean)
                          .join(" - ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(event) => {
                    setStartDate(event.target.value);
                    setReport(null);
                  }}
                />
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(event) => {
                    setEndDate(event.target.value);
                    setReport(null);
                  }}
                />
              </div>

              <div className="flex items-end">
                <Button
                  onClick={loadStageEvents}
                  disabled={loadingReport || loadingFilters}
                  className="w-full bg-slate-900 text-white hover:bg-slate-700 xl:w-auto"
                >
                  {loadingReport ? (
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="mr-2 h-4 w-4" />
                  )}
                  View Data
                </Button>
              </div>
            </div>

            {selectedAssetInfo && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {thresholdItems.map((item) => {
                  const styles = getStageStyles(item.type);
                  return (
                    <div
                      key={item.type}
                      className={`rounded-lg border px-4 py-3 ${styles.card}`}
                    >
                      <p className="text-xs font-semibold uppercase">
                        {item.label}
                      </p>
                      <p className="mt-1 text-xl font-bold">
                        {formatPercent(item.value)}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {report && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {summaryItems.map((item) => {
              const styles = getStageStyles(item.type);
              return (
                <Card key={item.type} className={styles.card}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-sm font-medium">{item.label} Events</p>
                      <p className="text-2xl font-bold">{item.count}</p>
                    </div>
                    {item.type === "critical" ? (
                      <AlertTriangle className={`h-8 w-8 ${styles.icon}`} />
                    ) : (
                      <Waves className={`h-8 w-8 ${styles.icon}`} />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Card>
          <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="text-lg">Daily Threshold Events</CardTitle>
              <CardDescription>
                {report
                  ? `${events.length} event${events.length === 1 ? "" : "s"} found for ${formatHumanDate(startDate)} to ${formatHumanDate(endDate)}`
                  : "Run a search to view the first daily critical, low, mid, and high threshold hits."}
              </CardDescription>
            </div>
            {report && (
              <Badge variant="outline" className="w-fit">
                Max range: {MAX_RANGE_DAYS} days
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            {!report ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center">
                <Bell className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                <p className="font-medium text-slate-700">No data loaded yet</p>
                <p className="mt-1 text-sm text-slate-500">
                  Choose filters and click View Data.
                </p>
              </div>
            ) : events.length === 0 ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-12 text-center">
                <CheckCircle className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
                <p className="font-medium text-emerald-800">
                  No stage events found
                </p>
                <p className="mt-1 text-sm text-emerald-700">
                  This storage asset did not hit configured thresholds in the
                  selected range.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700">
                          Stage
                        </th>
                        <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700">
                          Timestamp (IST)
                        </th>
                        <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700">
                          Level
                        </th>
                        <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700">
                          Threshold
                        </th>
                        <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700">
                          Asset Type
                        </th>
                        <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700">
                          Storage Asset
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {events.map((event, index) => {
                        const styles = getStageStyles(event.stage_type);
                        return (
                          <tr
                            key={`${event.timestamp}-${event.stage_type}-${index}`}
                            className="odd:bg-white even:bg-slate-50/70"
                          >
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${styles.badge}`}
                              >
                                {event.stage_label}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                              {event.datetime}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900">
                              {formatPercent(event.percentage)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                              {formatPercent(event.threshold)}
                            </td>
                            <td className="px-4 py-3 text-slate-700">
                              {selectedAssetType}
                            </td>
                            <td className="px-4 py-3 text-slate-700">
                              {selectedAssetName}
                              <span className="ml-1 text-slate-400">
                                ({selectedAssetId})
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Alerts;
